import React, { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import {
  FavoriteEntityType,
  IFavoriteVersionEntityMutation,
  IFavoriteVersionEntityMutationVariables,
} from '../../utils/apollo/graphqlSchema';
import { FAVORITE_VERSION_ENTITY } from '../../utils/apollo/favorite/mutations';
import printGraphqlErrors from '../../utils/apollo/printGraphqlErrors';
import { updateDictionaryItemAction } from '../../redux/slices/dictionary';
import { DictionaryTypeEnum } from '../../utils/interfaces';
import { useDispatch } from 'react-redux';
import { FavoriteStarIcon, IconWrapper } from '../Icon';

const favoriteEntityTypeToDictionaryType: Record<
  FavoriteEntityType,
  DictionaryTypeEnum | null
> = {
  ANALYSIS: null,
  BASIC_EVENT: DictionaryTypeEnum.DictionaryEvent,
  DASHBOARD: null,
  FIELD: DictionaryTypeEnum.DictionaryField,
  BUCKET_DEF: DictionaryTypeEnum.DictionaryBuckets,
  COHORT: DictionaryTypeEnum.DictionaryCohort,
  FILTER: DictionaryTypeEnum.DictionaryFilter,
  MEASURE: DictionaryTypeEnum.DictionaryMeasure,
  VIRTUAL_EVENT: DictionaryTypeEnum.DictionaryVirtualEvent,
};

interface IProps {
  entityType: FavoriteEntityType;
  entity: {
    id: string;
    isFavorite: boolean;
  };
  onRefresh?: () => void;
}

const FavoriteVersionEntity = ({ entityType, entity, onRefresh }: IProps) => {
  const dispatch = useDispatch();

  const [setFavorite] = useMutation<
    IFavoriteVersionEntityMutation,
    IFavoriteVersionEntityMutationVariables
  >(FAVORITE_VERSION_ENTITY);

  const toggleFavorite = useCallback(async () => {
    const res = await setFavorite({
      variables: {
        input: {
          entityId: entity.id,
          entityType,
          isFavorite: !entity.isFavorite,
        },
      },
    });
    const mutationRes = res?.data?.favoriteVersionEntity;
    if (printGraphqlErrors(mutationRes?.errors)) {
      return;
    }
    const dictionaryType = favoriteEntityTypeToDictionaryType[entityType];
    if (dictionaryType) {
      dispatch(
        updateDictionaryItemAction({
          item: { ...entity, isFavorite: !entity.isFavorite },
          dictionaryType,
        }),
      );
    }
    // refresh entities in the dictionary sidebar
    onRefresh?.();
  }, [dispatch, setFavorite, onRefresh, entity, entityType]);

  return (
    <IconWrapper>
      <FavoriteStarIcon isActive={entity.isFavorite} onClick={toggleFavorite} />
    </IconWrapper>
  );
};

export default FavoriteVersionEntity;
